'use client';

import { EmblaCarouselType } from 'embla-carousel';
import { VariantProps } from 'tailwind-variants';

import { PageBlock } from '@/payload/payload-types-block';
import { UiBannerPicture } from '@/ui/banner-picture';
import { UiLink } from '@/ui/link';
import { UiKitCarousel } from '@/ui-kit/carousel';
import { useMediaQuery } from '@/ui-tool/media-query';

import { styles } from './UiBannerCarouselMini.styles';

type BannerCarouselMini = PageBlock<'bannerCarouselMini'>;

export interface UiBannerCarouselMiniProps {
  banners: BannerCarouselMini['slides'];
  titleLink: BannerCarouselMini['titleLink'];
  countDesktopSlide?: BannerCarouselMini['countDesktopSlide'];
  onLinkClick?: (label?: string, skipMenuClick?: boolean) => void;
  onSlidesChange?: (index: number) => void;
  onBannerClick?: (index: number) => boolean | void;
  onCarouselInit?: (api: EmblaCarouselType) => void;
}

export type Quantity = VariantProps<typeof styles>['quantity'];

export function UiBannerCarouselMini({
  banners,
  titleLink,
  countDesktopSlide = '2.5',
  onLinkClick,
  onSlidesChange,
  onBannerClick,
  onCarouselInit
}: UiBannerCarouselMiniProps) {
  const isMiniMobile = useMediaQuery('sm', 'max');

  const slots = styles();

  const bannersTotal = banners.length;
  const quantity: Quantity =
    bannersTotal === 1
      ? 'one'
      : bannersTotal === 2
        ? 'couple'
        : countDesktopSlide === '2.5'
          ? 'many'
          : 'many-full';
  const desktopFieldName =
    bannersTotal === 1 ? 'banner-mini-desktop-full' : 'banner-mini-desktop';

  const isActiveCarousel =
    (!isMiniMobile && bannersTotal > 2) || (isMiniMobile && bannersTotal > 1);

  return (
    <div className={slots.base({ quantity })}>
      {titleLink && titleLink.label && (
        <div className={slots.titleWrapper()}>
          <UiLink
            link={titleLink}
            className={slots.title()}
            showIcon
            onClick={() => onLinkClick?.(titleLink.label ?? undefined)}
          />
        </div>
      )}

      <UiKitCarousel
        options={{ active: isActiveCarousel, watchDrag: true, inViewThreshold: 0.5 }}
        className={slots.carousel({
          quantity
        })}
        arrowNextClass={slots.arrowNext({ className: slots.arrow() })}
        arrowPrevClass={slots.arrowPrev({ className: slots.arrow() })}
        viewportClassName={slots.viewportCarousel({ quantity })}
        showArrows="always"
        arrowType="chevron"
        loop={false}
        onSlidesChange={onSlidesChange}
        onInit={onCarouselInit}
      >
        {banners.map((banner, index) => (
          <div
            key={banner.id}
            className={slots.banner()}
          >
            {typeof banner.image !== 'number' && banner.image.sizes && (
              <UiBannerPicture
                sizes={banner.image.sizes}
                customSizes={banner.image.customSizes}
                alt={banner.image.alt}
                className={slots.image()}
                desktopFieldName={desktopFieldName}
                mobileFieldName="banner-mini-mobile"
              />
            )}

            <div className={slots.content({ mode: banner.theme ?? 'light' })}>
              {banner.title && <div className={slots.bannerTitle()}>{banner.title}</div>}

              {banner.subtitle && (
                <div className={slots.bannerSubtitle()}>{banner.subtitle}</div>
              )}

              {banner?.link?.url && (
                <UiLink
                  link={banner.link}
                  className={slots.button()}
                  showIcon
                  onClick={() => {
                    const sentPromotion = onBannerClick?.(index);
                    onLinkClick?.(
                      banner.title || banner.link?.label || undefined,
                      Boolean(sentPromotion)
                    );
                  }}
                />
              )}
            </div>

            {banner?.link?.url && (
              <UiLink
                link={banner.link}
                className={slots.link()}
                onClick={() => {
                  const sentPromotion = onBannerClick?.(index);
                  onLinkClick?.(
                    banner.title || banner.link?.label || undefined,
                    Boolean(sentPromotion)
                  );
                }}
              />
            )}
          </div>
        ))}
      </UiKitCarousel>
    </div>
  );
}
